import React, { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { devLog } from 'Util/_HumanWritten/ConsoleLogUtils';
import { stringIsEmpty } from 'Util/TypeGuards';
import { newAlertModal } from 'Views/Components/Modal/NewAlertModal';
import { isTripClosedByBookingType, TicketsTabStateData } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { store } from 'Models/Store';

// Import your existing validation hooks
import { useTicketDeselectionForFilterDates } from './useTicketDeselectionForFilterDates';
import { useTicketDeselectionForInvalidReturn } from './useTicketDeselectionForInvalidReturn';
import { useTicketDeselectionForFilterLocations } from './useTicketDeselectionForFilterLocations';
import { useTicketDeselectionWhenPaxExceeds } from './useTicketDeselectionWhenPaxExceeds';
import { useTicketDeselectionForClosedTrip } from './useTicketDeselectionForClosedTrip';
import { BookingWizardData } from '../../../BookingWizardData/BaseBookingWizardData';

interface UseTripValidationProps {
	wizardData: BookingWizardData;
	setWizardData: (data: BookingWizardData) => void;
	trips: TicketsTabStateData;
	departureTripsLoading: boolean;
	destinationTripsLoading: boolean;
	oldWizardData: BookingWizardData | null;
	/** Holds OverbookInBookingWizard, so a sold-out sailing stays selected. */
	canOverbook: boolean;
}

export function useTripValidation({
	wizardData,
	setWizardData,
	trips,
	departureTripsLoading,
	destinationTripsLoading,
	oldWizardData,
	canOverbook,
}: UseTripValidationProps) {
	const selectedDepartingTrip = trips.departureTickets?.find(x => x.id === wizardData.departureTicketId);
	const selectedReturningTrip = trips.returningTickets?.find(x => x.id === wizardData.returningTicketId);

	// Track whether trips have been successfully loaded at least once
	const initialTripsLoaded = useRef(false);
	const initialDepartureTicketId = useRef(wizardData.departureTicketId);
	const initialReturningTicketId = useRef(wizardData.returningTicketId);

	// Use existing validation hooks
	useTicketDeselectionForFilterDates(
		wizardData,
		setWizardData,
		selectedDepartingTrip?.departureDateTime,
		selectedReturningTrip?.departureDateTime,
	);

	useTicketDeselectionForInvalidReturn(
		wizardData,
		setWizardData,
		selectedDepartingTrip?.departureDateTime,
		selectedReturningTrip?.departureDateTime,
	);

	useTicketDeselectionForFilterLocations(wizardData, setWizardData);

	useTicketDeselectionWhenPaxExceeds(
		wizardData,
		oldWizardData,
		setWizardData,
		selectedDepartingTrip,
		selectedReturningTrip,
	);

	useTicketDeselectionForClosedTrip(wizardData, setWizardData, selectedDepartingTrip, selectedReturningTrip);

	// Validate that previously selected trips still exist
	useEffect(() => {
		if (departureTripsLoading || destinationTripsLoading) {
			return;
		}

		// Check if we need to wait for both trip types
		const expectReturnTrips = wizardData.tripType === 'return';
		const departureTripsReady = trips.departureTickets !== null && trips.departureTickets.length > 0;
		const returnTripsReady =
			!expectReturnTrips || (trips.returningTickets !== null && trips.returningTickets.length > 0);

		// Wait until both trip arrays have actual data (not just empty arrays)
		if (!initialTripsLoaded.current && departureTripsReady && returnTripsReady) {
			initialTripsLoaded.current = true;
			return; // Skip validation on the very first successful load
		}

		// Don't validate until we've loaded trips at least once
		if (!initialTripsLoaded.current) {
			return;
		}

		// Check departing trip
		if (wizardData.departureTicketId && trips.departureTickets !== null) {
			// Skip validation for initial URL param trip IDs
			const isInitialUrlTrip = wizardData.departureTicketId === initialDepartureTicketId.current;

			const tripStillExists = trips.departureTickets.some(trip => trip.id === wizardData.departureTicketId);

			if (!tripStillExists && !isInitialUrlTrip) {
				const newData = new BookingWizardData({ ...wizardData });
				newData.departureTicketId = '';
				newData.departingJourney = undefined;
				setWizardData(newData);
				// Silently deselect - no popup needed as the trip may just be outside the date range
			}
		}

		// Check returning trip
		if (wizardData.returningTicketId && trips.returningTickets !== null && wizardData.tripType === 'return') {
			// Skip validation for initial URL param trip IDs
			const isInitialUrlTrip = wizardData.returningTicketId === initialReturningTicketId.current;

			const tripStillExists = trips.returningTickets.some(trip => trip.id === wizardData.returningTicketId);

			if (!tripStillExists && !isInitialUrlTrip) {
				const newData = new BookingWizardData({ ...wizardData });
				newData.returningTicketId = '';
				newData.returningJourney = undefined;
				setWizardData(newData);
				// Silently deselect - no popup needed as the trip may just be outside the date range
			}
		}
	}, [
		trips.departureTickets,
		trips.returningTickets,
		departureTripsLoading,
		destinationTripsLoading,
		wizardData,
		wizardData.departureTicketId,
		wizardData.returningTicketId,
		wizardData.tripType,
		setWizardData,
	]);

	// Validate trip capacity (sold out)
	useEffect(() => {
		if (stringIsEmpty(wizardData.bookingToEdit) && !canOverbook && selectedDepartingTrip) {
			const isVehicleSoldOut =
				wizardData.tabSelected === 'vehicle' && (selectedDepartingTrip.vehicleSpacesAvailable ?? 0) < 1;
			const isPassengerSoldOut =
				wizardData.tabSelected === 'passenger' && (selectedDepartingTrip.passengerSpacesAvailable ?? 0) === 0;
			const isSoldOut = isVehicleSoldOut || isPassengerSoldOut;

			if (isSoldOut) {
				const newData = new BookingWizardData({ ...wizardData });
				newData.departureTicketId = '';
				newData.departingJourney = undefined;
				devLog('DEV0012: Deselected departing ticket - sold out');
				setWizardData(newData);
			}
		}
	}, [selectedDepartingTrip, wizardData, wizardData.bookingToEdit, wizardData.tabSelected, canOverbook, setWizardData]);

	// Validate trip is not closed
	//
	// Gated on staff, not manager. The server lets every staff member book a closed trip (the add and
	// alter pipelines skip the closed check for any staff caller), and `useIsLegSelectable` /
	// `useLegOptionClick` already let staff pick one. Gating this on `isManager` left plain Staff in
	// between: the click selected the trip and this effect unselected it on the next render, which
	// showed up as the wizard steps flickering and the selection never sticking. Most visible on a
	// vessel that doesn't offer walk-ons, where every walk-on trip reads as closed.
	useEffect(() => {
		if (
			stringIsEmpty(wizardData.bookingToEdit) &&
			!store.can('OverrideBookingRestrictions') &&
			selectedDepartingTrip
		) {
			const isClosed = isTripClosedByBookingType(selectedDepartingTrip, wizardData.tabSelected);

			if (isClosed) {
				const newData = new BookingWizardData({ ...wizardData });
				newData.departureTicketId = '';
				newData.departingJourney = undefined;
				devLog('DEV0018: Deselected departing ticket - closed');
				setWizardData(newData);
			}
		}
	}, [selectedDepartingTrip, wizardData, wizardData.bookingToEdit, wizardData.tabSelected, setWizardData]);

	// Handle waitlist modal (from redirected email)
	useEffect(() => {
		if (
			store?.redirectedFromWaitlistEmail === true &&
			trips.departureTickets !== null &&
			trips.returningTickets !== null
		) {
			if (wizardData.departureTicketId === '') {
				newAlertModal(
					<FormattedMessage
						id="booking-wizard__tickets__waitlist-missed__title"
						defaultMessage="You've just missed out"
					/>,
					<h5>
						<FormattedMessage
							id="booking-wizard__tickets__waitlist-missed__description"
							defaultMessage="Spaces fill up quick, sorry about that. You're still on the waitlist so we'll email you when space becomes available."
						/>
					</h5>,
					{ cancelText: 'OK' },
				)
					.then(() => {})
					.catch(() => {});
			} else {
				newAlertModal(
					<FormattedMessage
						id="booking-wizard__tickets__waitlist-available__title"
						defaultMessage="Spaces available!"
					/>,
					<h5>
						<FormattedMessage
							id="booking-wizard__tickets__waitlist-available__description"
							defaultMessage="Good news - there is space available on this ferry, so we've preselected your trip for you to book with ease."
						/>
					</h5>,
					{ cancelText: "Let's go" },
				)
					.then(() => {})
					.catch(() => {});
			}

			store.setRedirectedFromWaitlistEmail(false);
		}
	}, [trips.departureTickets, trips.returningTickets, wizardData.departureTicketId]);
}
